import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined'
import JSZip from 'jszip'
import { FC, useCallback, useState } from 'react'
import { AnalyticsEvent } from 'utils/analytics/AnalyticsEvent'
import { MUIButton } from 'components/common/MUIButton'
import { ensureFilenameExtension, hasFilenameExtension } from 'utils/helpers'
import { logAnalyticsEvent } from 'utils/analytics/analyticsUtils'
import { logger } from 'utils/logging'
import { saveAs } from 'file-saver'
import { useTranslation } from 'react-i18next'

export interface BulkDownloadFile {
  url: string
  filename: string
}

interface Props {
  zipName: string
  getFiles: () => Promise<BulkDownloadFile[]>
}

/**
 * Bulk download button. Fetches each file as a blob, packages them into a single zip,
 * and saves the archive client-side so the user gets one file instead of N tabs.
 * `getFiles` is called on click — callers should resolve fresh signed URLs there
 * because signed URLs expire after 1 hour.
 * @example
 * <DownloadAllFilesButton zipName={assignmentId} getFiles={resolveSignedUrls} />
 */
export const DownloadAllFilesButton: FC<Props> = ({ zipName, getFiles }) => {
  const { t } = useTranslation(['gallery'])
  const [isDownloading, setIsDownloading] = useState(false)

  const handleDownloadAll = useCallback(async () => {
    setIsDownloading(true)
    const startedAt = performance.now()

    logAnalyticsEvent(AnalyticsEvent.ASSIGNMENT_DETAIL_DOWNLOAD_BULK_STARTED, { zipName })

    try {
      const files = await getFiles()
      if (!files.length) return

      const zip = new JSZip()
      const folder = zip.folder(zipName)

      for (const file of files) {
        // Defense-in-depth: only fetch https URLs, skip anything else (blob:, http:, data:, file:)
        if (!file.url.startsWith('https://')) {
          logger.warn('Bulk download skipped non-https url', undefined, { context: { component: 'DownloadAllFilesButton', metadata: { zipName, urlPrefix: file.url.slice(0, 8) } } })
          continue
        }

        const response = await fetch(file.url)
        if (!response.ok) {
          logger.warn('Bulk download fetch returned non-ok status', undefined, { context: { component: 'DownloadAllFilesButton', metadata: { zipName, status: response.status, filename: file.filename } } })
          continue
        }

        const blob = await response.blob()
        // Sanitize filename to prevent Zip Slip — strip path separators and leading dots
        const safeFilename = file.filename.replace(/[\\/]/g, '_').replace(/^\.+/, '')
        // Defense-in-depth: derive a missing extension from the URL pathname or Content-Type.
        const contentType = response.headers.get('content-type')
        const namedFilename = ensureFilenameExtension(safeFilename, { url: file.url, contentType })
        // Regression signal for QA-205: warn if no source could resolve an extension so we catch recurrences in production.
        if (!hasFilenameExtension(namedFilename)) {
          logger.warn('Bulk download zip entry has no extension after fallbacks', undefined, { context: { component: 'DownloadAllFilesButton', metadata: { zipName, filename: file.filename, contentType } } })
        }
        folder?.file(namedFilename, blob)
      }

      const content = await zip.generateAsync({ type: 'blob' })
      saveAs(content, `${zipName}.zip`)

      logAnalyticsEvent(AnalyticsEvent.ASSIGNMENT_DETAIL_DOWNLOAD_BULK_SUCCEEDED, {
        zipName,
        fileCount: files.length,
        durationMs: Math.round(performance.now() - startedAt),
      })
    } catch (error) {
      logger.error('Bulk download failed', error, { context: { component: 'DownloadAllFilesButton', metadata: { zipName } } })
      logAnalyticsEvent(AnalyticsEvent.ASSIGNMENT_DETAIL_DOWNLOAD_BULK_FAILED, {
        zipName,
        durationMs: Math.round(performance.now() - startedAt),
      })
    } finally {
      setIsDownloading(false)
    }
  }, [getFiles, zipName])

  return (
    <MUIButton
      type="secondaryBorder"
      variant="outlined"
      startIcon={<FileDownloadOutlinedIcon />}
      isLoading={isDownloading}
      onClick={handleDownloadAll}
    >
      {t('gallery:assignment_details.download_all_files')}
    </MUIButton>
  )
}
